import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, FilterCard, Field, SimpleSelect, EmptyState, KpiCard } from "@/components/common";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  Siren, FileText, Ambulance, Timer, Gauge, Target, BarChart3, RotateCcw,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Cell, LabelList,
} from "recharts";
import { monthStartISO, todayISO, fmtTanggal, alasanCodeBlueText } from "@/lib/siport";
import { EwsBadge } from "@/pages/siport/SiportData";

const ALL = "__all__";
// Palet kategorikal tervalidasi (CVD & kontras) — urutan tetap mengikuti urutan master tindak lanjut.
const SERIES_COLORS = ["#0891B2", "#D97706", "#BE123C"];
const OTHER_COLOR = "#64748B";
const BAR_COLOR = "hsl(var(--brand-teal))";
const AXIS_TICK = { fontSize: 12, fill: "#64748B" };

function ChartCard({ title, children, className = "", testId }) {
  return (
    <Card className={`border-slate-200 shadow-sm ${className}`} data-testid={testId}>
      <CardHeader className="pb-2"><CardTitle className="text-base font-semibold">{title}</CardTitle></CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// Distribusi per kategori: bar horizontal, label nilai di ujung bar
function DistBar({ data, colorKey, height }) {
  const rows = (data || []).map((d) => ({ ...d, name: d.label }));
  if (!rows.some((r) => r.jumlah > 0)) return <EmptyState title="Belum ada data" />;
  const h = height || Math.max(160, rows.length * 38 + 20);
  return (
    <div style={{ width: "100%", height: h }}>
      <ResponsiveContainer>
        <BarChart data={rows} layout="vertical" margin={{ top: 0, right: 36, left: 0, bottom: 0 }} barCategoryGap={6}>
          <CartesianGrid horizontal={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} />
          <YAxis type="category" dataKey="name" width={170} tick={{ ...AXIS_TICK, fill: "#334155" }} interval={0} />
          <Tooltip cursor={{ fill: "rgba(148,163,184,0.15)" }} formatter={(v) => [v, "Jumlah"]} />
          <Bar dataKey="jumlah" radius={[0, 4, 4, 0]} maxBarSize={26}>
            {rows.map((r) => <Cell key={r.name} fill={colorKey && r[colorKey] ? r[colorKey] : BAR_COLOR} />)}
            <LabelList dataKey="jumlah" position="right" style={{ fontSize: 12, fill: "#334155", fontWeight: 600 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function SiportDashboard() {
  const { isSiportViewer } = useAuth();
  const [filter, setFilter] = useState({
    tanggal_dari: monthStartISO(), tanggal_sampai: todayISO(), poliklinik: ALL, tahun: String(new Date().getFullYear()),
  });
  const [poliNames, setPoliNames] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/siport/poliklinik").then((r) => setPoliNames(r.data));
  }, []);

  useEffect(() => {
    const p = new URLSearchParams();
    if (filter.tanggal_dari) p.set("tanggal_dari", filter.tanggal_dari);
    if (filter.tanggal_sampai) p.set("tanggal_sampai", filter.tanggal_sampai);
    if (filter.poliklinik !== ALL) p.set("poliklinik", filter.poliklinik);
    if (filter.tahun) p.set("tahun", filter.tahun);
    api.get(`/siport/dashboard?${p}`).then((r) => setData(r.data))
      .catch((err) => toast.error(apiError(err.response?.data?.detail)));
  }, [filter]);

  const setF = (k) => (v) => setFilter((f) => ({ ...f, [k]: v }));
  const k = data?.kpi;
  const target = data?.target_respon_menit;
  const series = data?.trend_series || [];
  const colorOf = (i) => SERIES_COLORS[i] || OTHER_COLOR;
  const trend = (data?.trend || []).map((t) => ({ ...t, name: t.bulan_nama.slice(0, 3) }));
  const respon = (data?.respon || []).filter((r) => r.respon_time_menit != null).map((r) => ({
    ...r, name: `${fmtTanggal(r.tanggal_kejadian).slice(0, 5)} · ${r.nomor_laporan.slice(-4)}`,
  }));
  const fmtMenit = (v) => (v != null ? `${v} mnt` : "—");

  return (
    <div data-testid="siport-dashboard-page">
      <PageHeader icon={Siren} title="Dashboard SIPORT"
        subtitle="Rekap percepatan layanan pasien & aktivasi Code Blue rawat jalan IPRJT." />

      <FilterCard>
        <Field label="Tanggal Input dari">
          <Input type="date" value={filter.tanggal_dari} onChange={(e) => setF("tanggal_dari")(e.target.value)} className="w-[150px] bg-white" data-testid="siport-dash-dari" />
        </Field>
        <Field label="Tanggal Input sampai">
          <Input type="date" value={filter.tanggal_sampai} onChange={(e) => setF("tanggal_sampai")(e.target.value)} className="w-[150px] bg-white" data-testid="siport-dash-sampai" />
        </Field>
        <Field label="Poliklinik">
          <SimpleSelect value={filter.poliklinik} onChange={setF("poliklinik")} className="w-[220px]" testId="siport-dash-poli"
            options={[{ label: "Semua Poliklinik", value: ALL }, ...poliNames.map((n) => ({ label: n, value: n }))]} />
        </Field>
        <Field label="Tahun (grafik tren)">
          <SimpleSelect value={filter.tahun} onChange={setF("tahun")} className="w-[120px]" testId="siport-dash-tahun"
            options={(data?.tahun_list || [filter.tahun]).map((t) => ({ label: String(t), value: String(t) }))} />
        </Field>
        <Button variant="ghost" onClick={() => setFilter((f) => ({ ...f, tanggal_dari: monthStartISO(), tanggal_sampai: todayISO() }))}>
          <RotateCcw className="w-4 h-4 mr-2" />Bulan ini
        </Button>
      </FilterCard>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-6" data-testid="siport-kpi">
        <KpiCard icon={FileText} label="Total Laporan" value={k?.total ?? "—"} tone="slate" />
        <KpiCard icon={Siren} label="Aktivasi Code Blue" value={k?.code_blue ?? "—"} tone="rose" />
        <KpiCard icon={Ambulance} label="Transfer IGD" value={k?.transfer_igd ?? "—"} tone="amber" />
        <KpiCard icon={Timer} label="Rata-rata Respon TMRC" value={k?.respon_rata2 != null ? `${k.respon_rata2} mnt` : "—"} tone="blue"
          sub={k ? `dari ${k.code_blue_dengan_respon} kejadian berdata jam` : null} />
        <KpiCard icon={Gauge} label="Respon Tercepat / Terlama" value={k ? `${fmtMenit(k.respon_min)} / ${fmtMenit(k.respon_max)}` : "—"} tone="slate" />
        <KpiCard icon={Target} label="Code Blue ≤ Target" tone="emerald"
          value={k?.persen_on_target != null ? `${(k.persen_on_target * 100).toFixed(1)}%` : "—"}
          sub={target != null ? `target ≤ ${target} menit`
            : isSiportViewer ? "Target belum diatur" : <Link to="/siport/pengaturan" className="underline">Target belum diatur</Link>} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title={`Tren Jumlah Laporan per Bulan — ${data?.tahun ?? ""}`} className="lg:col-span-2" testId="siport-chart-trend">
          {trend.some((t) => t.total > 0) ? (
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <BarChart data={trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={AXIS_TICK} />
                  <YAxis allowDecimals={false} tick={AXIS_TICK} />
                  <Tooltip cursor={{ fill: "rgba(148,163,184,0.15)" }} />
                  <Legend />
                  {series.map((s, i) => (
                    <Bar key={s} dataKey={s} stackId="tl" fill={colorOf(i)} stroke="#fff" strokeWidth={1}
                      radius={i === series.length - 1 ? [4, 4, 0, 0] : 0} maxBarSize={44} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <EmptyState icon={BarChart3} title="Belum ada laporan di tahun ini" />}
        </ChartCard>

        <ChartCard title="Distribusi Score EWS" testId="siport-chart-ews">
          <DistBar data={data?.ews_score} colorKey="warna" />
        </ChartCard>
        <ChartCard title="Distribusi Tindak Lanjut">
          <DistBar data={data?.tindak_lanjut} />
        </ChartCard>
        <ChartCard title="Distribusi Penyakit Dasar">
          <DistBar data={data?.penyakit_dasar} />
        </ChartCard>
        <ChartCard title="Distribusi Kriteria Masalah">
          <DistBar data={data?.kriteria_masalah} />
        </ChartCard>
        <ChartCard title="Jenis & Alasan Percepatan">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Jenis Percepatan</div>
          <DistBar data={data?.jenis_percepatan} />
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-4 mb-1">Alasan Percepatan</div>
          <DistBar data={data?.alasan_percepatan} />
        </ChartCard>
        <ChartCard title="Laporan per Poliklinik (Top 10)">
          <DistBar data={data?.poliklinik} />
        </ChartCard>
        <ChartCard title="Alasan Aktivasi Code Blue" testId="siport-chart-alasan-cb">
          <p className="text-xs text-slate-500 -mt-1 mb-2">Satu kejadian bisa memiliki lebih dari satu alasan.</p>
          <DistBar data={data?.alasan_code_blue} />
        </ChartCard>

        <ChartCard title="Respon Time TMRC per Kejadian Code Blue" testId="siport-chart-respon">
          {respon.length ? (
            <>
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={respon} margin={{ top: 16, right: 16, left: -10, bottom: 0 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ ...AXIS_TICK, fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={AXIS_TICK} unit="m" />
                    <Tooltip cursor={{ fill: "rgba(148,163,184,0.15)" }}
                      formatter={(v) => [`${v} menit`, "Respon time"]}
                      labelFormatter={(_, p) => (p?.[0] ? `${p[0].payload.nomor_laporan} · ${p[0].payload.poliklinik_nama}` : "")} />
                    <Bar dataKey="respon_time_menit" radius={[4, 4, 0, 0]} maxBarSize={32}>
                      {respon.map((r) => (
                        <Cell key={r.nomor_laporan} fill={target != null && r.respon_time_menit > target ? "#D97706" : "#0891B2"} />
                      ))}
                    </Bar>
                    {target != null && (
                      <ReferenceLine y={target} stroke="#BE123C" strokeDasharray="6 4" strokeWidth={2}
                        label={{ value: `Target ${target} mnt`, position: "insideTopRight", fill: "#BE123C", fontSize: 12 }} />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              {target != null && (
                <div className="flex gap-4 text-xs text-slate-600 mt-2">
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "#0891B2" }} />≤ target</span>
                  <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: "#D97706" }} />melebihi target</span>
                </div>
              )}
            </>
          ) : <EmptyState icon={Timer} title="Belum ada kejadian Code Blue berdata jam" />}
        </ChartCard>
      </div>

      <Card className="border-slate-200 shadow-sm mt-6" data-testid="siport-recent-cb">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base font-semibold">10 Kejadian Code Blue Terakhir</CardTitle>
          <Link to="/siport/data" className="text-sm text-primary hover:underline">Lihat semua data →</Link>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Tanggal Input</TableHead>
                  <TableHead>No. Laporan</TableHead>
                  <TableHead>Poliklinik</TableHead>
                  <TableHead>Pasien</TableHead>
                  <TableHead>EWS</TableHead>
                  <TableHead>Alasan</TableHead>
                  <TableHead>Aktivasi → TMRC</TableHead>
                  <TableHead>Respon</TableHead>
                  <TableHead>Akhir Code Blue</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.recent_code_blue || []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="whitespace-nowrap">{fmtTanggal(r.tanggal_kejadian)}</TableCell>
                    <TableCell className="font-mono text-xs">{r.nomor_laporan}</TableCell>
                    <TableCell>{r.poliklinik_nama}</TableCell>
                    <TableCell>
                      <div className="font-medium">{r.nama_pasien}</div>
                      <div className="text-xs text-slate-500">{r.no_rm}</div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap"><EwsBadge ews={r.ews_score} /></TableCell>
                    <TableCell className="max-w-[180px]">{alasanCodeBlueText(r.code_blue)}</TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {r.code_blue?.jam_aktivasi || "—"} → {r.code_blue?.jam_tmrc_datang || "—"}
                    </TableCell>
                    <TableCell className={`tabular-nums font-semibold whitespace-nowrap ${
                      target != null && r.code_blue?.respon_time_menit > target ? "text-amber-700" : "text-slate-900"}`}>
                      {fmtMenit(r.code_blue?.respon_time_menit)}
                    </TableCell>
                    <TableCell className="max-w-[240px] text-sm text-slate-600">{r.code_blue?.akhir_code_blue}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data && !data.recent_code_blue.length && <EmptyState icon={Siren} title="Tidak ada kejadian Code Blue pada periode ini" />}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
